// src/pages/DownpipeProduct.jsx
import React, { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DOWNPIPE_PRODUCTS } from "../data/downpipeProducts";
import { EXHAUST_PRODUCTS } from "../data/exhaustProducts";

// Build a list of candidate images from the main image path:
// tries base, base-1..base-4; each candidate falls back /p-dp- -> /p-exh-
function buildMedia(product) {
  const src = product?.img;
  if (!src) return [];
  const m = src.match(/^(.*)(\.[a-zA-Z0-9]+)$/);
  const base = m ? m[1] : src.replace(/\.[a-zA-Z0-9]+$/, "");
  const ext = m ? m[2] : ".jpg";
  return [base, `${base}-1`, `${base}-2`, `${base}-3`, `${base}-4`].map(
    (b) => `${b}${ext}`
  );
}

function fallbackIfExh(path) {
  if (!path) return path;
  return path.includes("/assets/p-dp-")
    ? path.replace("/assets/p-dp-", "/assets/p-exh-")
    : path;
}
function dpToExhaustSlug(dpSlug = "") {
  const rest = dpSlug.replace(/^ul-dp-/i, "");
  if (rest === "911") return "ul-valved-911";
  return `ul-catback-${rest}`;
}

export default function DownpipeProduct({ onAddToCart }) {
  const { slug } = useParams();
  const navigate = useNavigate();

  // Define product FIRST
  const product = DOWNPIPE_PRODUCTS.find((p) => p.slug === slug);
  const relatedExhaustSlug = dpToExhaustSlug(slug);
  const relatedExhaust = EXHAUST_PRODUCTS.find((x) => x.slug === relatedExhaustSlug);

  // Hooks (always run)
  const [catalyst, setCatalyst] = useState("Race Catless"); // default option
  const [activeIdx, setActiveIdx] = useState(0);

  // Media
  const media = useMemo(() => buildMedia(product), [product]);

  // Pricing:
  // - basePrice = Race Catless price from data
  // - up = per-car upcharge (from data) OR derived from cattedPrice if present
  const basePrice = Number(product?.basePrice ?? 0);
  const up = useMemo(() => {
    const explicitUp = Number(product?.cattedUpcharge ?? 0);
    if (explicitUp > 0) return explicitUp;
    const catted = Number(product?.cattedPrice ?? 0);
    return catted > basePrice ? catted - basePrice : 0;
  }, [product, basePrice]);

  const displayPrice = useMemo(
    () => (catalyst === "High-Flow Catted" ? basePrice + up : basePrice),
    [basePrice, up, catalyst]
  );

  return (
    <main className="mx-auto max-w-7xl px-4 py-10">
      <button
        className="text-sm text-neutral-500 hover:text-neutral-900 mb-6"
        onClick={() => navigate(-1)}
      >
        ← Back
      </button>

      {!product ? (
        <div className="p-10">
          Product not found.{" "}
          <button className="underline" onClick={() => navigate("/downpipes")}>
            Back to Downpipes
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-8 items-start">
          {/* Media + Carousel */}
          <div className="space-y-4">
            {/* Main image */}
            {media.length > 0 ? (
              <img
                src={media[activeIdx]}
                alt={product.name}
                className="w-full rounded-2xl border border-neutral-200"
                onError={(e) => {
                  const fb = fallbackIfExh(e.currentTarget.src);
                  if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
                }}
              />
            ) : (
              <div className="w-full rounded-2xl border border-neutral-200 aspect-video bg-neutral-50" />
            )}

            {/* Thumbnails */}
            {media.length > 1 && (
              <div className="flex gap-2 overflow-x-auto pb-1">
                {media.map((url, i) => (
                  <button
                    key={url}
                    type="button"
                    onClick={() => setActiveIdx(i)}
                    className={[
                      "relative shrink-0 rounded-xl overflow-hidden border",
                      i === activeIdx
                        ? "border-black ring-2 ring-black/10"
                        : "border-neutral-200 hover:border-neutral-400",
                    ].join(" ")}
                    style={{ width: 80, height: 80 }}
                    aria-label={`Thumbnail ${i + 1}`}
                  >
                    <img
                      src={url}
                      alt=""
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const fb = fallbackIfExh(e.currentTarget.src);
                        if (fb && e.currentTarget.src !== fb) e.currentTarget.src = fb;
                      }}
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div className="space-y-4">
            <h1 className="text-3xl font-bold">{product.name} — Downpipes</h1>
            <div className="text-neutral-500">Starting at (Race Catless)</div>
            <div className="text-3xl font-extrabold">${displayPrice.toLocaleString()}</div>

            {/* ONE dropdown only (Catalyst) */}
            <div>
              <label className="block text-sm font-medium mb-1">Catalyst</label>
              <select
                className="w-full rounded-xl border border-neutral-300 px-3 py-2"
                value={catalyst}
                onChange={(e) => setCatalyst(e.target.value)}
              >
                <option>Race Catless</option>
                <option>High-Flow Catted</option>
              </select>
              <p className="text-xs text-neutral-500 mt-1">
                High-Flow Catted adds ${up}.
              </p>
            </div>

            <button
              className="mt-2 px-6 py-3 bg-black text-white rounded-xl font-semibold hover:bg-neutral-800 transition"
              onClick={() => {
                const cartItem = {
                  id: `dp_${product.slug}_${catalyst}`,
                  name: `${product.name} — Downpipes`,
                  price: displayPrice,
                  imageUrl: media[activeIdx] || product.img || "/assets/downpipe.jpg",
                  options: { catalyst },
                  slug: product.slug,
                  category: "downpipes",
                };
                onAddToCart?.(cartItem);
              }}
            >
              Add to Cart
            </button>
{/* Cross-sell: Pairs perfectly with */}
{relatedExhaust && (
  <div className="mt-8">
    <h3 className="text-lg font-semibold mb-3">Pairs perfectly with</h3>

    <div className="flex items-center gap-4 rounded-2xl border border-neutral-200 p-3">
      <img
        src={relatedExhaust.img}
        alt={relatedExhaust.name}
        className="w-24 h-24 object-cover rounded-xl border border-neutral-200"
      />
      <div className="flex-1">
        <div className="font-medium">{relatedExhaust.name} — Exhaust</div>
        <div className="text-sm text-neutral-600">
          From ${Number(relatedExhaust.basePrice || 0).toLocaleString()}
        </div>
        <div className="mt-2">
          <button
            className="px-3 py-2 text-sm rounded-lg border border-neutral-300 hover:bg-neutral-50"
            onClick={() => navigate(`/exhaust/${relatedExhaust.slug}`)}
          >
            View Exhaust
          </button>
        </div>
      </div>
    </div>
  </div>
)}

          </div>
        </div>
      )}
    </main>
  );
}
